import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import {useTranslation} from 'react-i18next';
import './../../PaymentHistory/CompactView/PaymentsCard.scss';
import FieldValue from '@components/system/FieldValue/FieldValue';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import HelperUtils from '@shared/utils/HelperUtils';
import {
  getPaymentPlanFrequencyLabel,
  PaymentPlanStatus
} from '@shared/constants/Mapping';
import {Link} from '@material-ui/core';
import {useSelector} from 'react-redux';
import {cancelCurrentPaymentPlan} from '@shared/services/DueInvoiceServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';

const useStyles = makeStyles({
  root: {
    minWidth: 275
  },
  content: {
    paddingBottom: '16px !important'
  },
  containerWrap: {
    padding: '4px 0'
  },
  flexingItems: {
    flex: '1'
  },
  statusStyle: {
    display: 'flex',
    flexDirection: 'row'
  },
  cancelPlanLinkStyle: {
    paddingTop: '13px',
    paddingLeft: '1.5rem'
  }
});
interface PaymentPlanCardDetailsProps {
  activePlandetails: any;
  totalPaidAmount: number;
  totalPlanAmount: number;
  guarantorId: number | string;
  paymentPlanDetails: any;
  forceUpdateHandler: any;
  cancelPaymentPlan?: boolean;
}
const PaymentPlanCardDetails = (props: PaymentPlanCardDetailsProps): any => {
  const classes = useStyles();
  const {
    activePlandetails,
    totalPaidAmount,
    totalPlanAmount,
    guarantorId,
    paymentPlanDetails,
    forceUpdateHandler,
    cancelPaymentPlan
  } = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const groupId: any = useSelector((state: any) => state.auth.groupId);
  const acOfClinics: any = useSelector(
    (state: any) => state.clinics.selectedClinicId || []
  );
  const [loader, setLoader] = React.useState(false);
  const [openDialogBox, setOpenDialogBox] = useState<boolean>(false);
  const [upcomingPaymentPlanDate, setUpcomingPaymentPlanDate]: any = useState(
    null
  );
  const [previousPaymentPlanDate, setPreviousPaymentPlanDate]: any = useState(
    null
  );

  useEffect(() => {
    setLoader(false);
    if (
      activePlandetails &&
      activePlandetails?.plan_execution_history?.length > 0
    ) {
      let sortedUpcomingInstalments: any = [
        ...activePlandetails.plan_execution_history
      ];
      let sortedPreviousInstalments: any = [
        ...activePlandetails.plan_execution_history
      ];

      // Sort the upcoming instalments by instalment_date in ascending order
      sortedUpcomingInstalments = sortedUpcomingInstalments.sort(
        (a: any, b: any) =>
          new Date(a.instalment_date).getTime() -
          new Date(b.instalment_date).getTime()
      );

      // Sort the upcoming instalments by instalment_date in descending order
      sortedPreviousInstalments = sortedPreviousInstalments.sort(
        (a: any, b: any) =>
          new Date(b.instalment_date).getTime() -
          new Date(a.instalment_date).getTime()
      );

      // Filter for pending payment plans after sorting
      const pendingPaymentPlans: any = sortedUpcomingInstalments.filter(
        (item: any) => item.status.toLowerCase() === PaymentPlanStatus.PENDING
      );

      // Filter for paid payment plans after sorting
      const paidPaymentPlans: any = sortedPreviousInstalments.filter(
        (item: any) =>
          item.status.toLowerCase() === PaymentPlanStatus.PAID ||
          item.status.toLowerCase() === PaymentPlanStatus.PARTIALLY_PAID
      );

      // Get today's date in 'YYYY-MM-DD' format based on local timezone
      const today: any = new Date().toLocaleDateString('en-CA'); // This will return the date in 'YYYY-MM-DD' format.

      // Find all payment plans where instalment_date >= today's date (compare as strings)
      const validPendingPaymentPlans: any = pendingPaymentPlans.filter(
        (item: any) => item.instalment_date >= today
      );

      if (validPendingPaymentPlans && validPendingPaymentPlans?.length > 0) {
        setUpcomingPaymentPlanDate(validPendingPaymentPlans[0]);
      }

      if (validPendingPaymentPlans && validPendingPaymentPlans?.length === 0) {
        setUpcomingPaymentPlanDate(null);
      }

      if (paidPaymentPlans && paidPaymentPlans?.length > 0) {
        setPreviousPaymentPlanDate(paidPaymentPlans[0]);
      }

      if (paidPaymentPlans && paidPaymentPlans?.length === 0) {
        setPreviousPaymentPlanDate(null);
      }
    } else {
      setUpcomingPaymentPlanDate(null);
      setPreviousPaymentPlanDate(null);
    }
  }, [activePlandetails]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleCancelPaymentPlan = async (PlanID: any): Promise<void> => {
    try {
      setLoader(true);
      const cancelPaymentPlanResponse = await cancelCurrentPaymentPlan(
        groupId,
        acOfClinics[0],
        {
          guarantor_id: guarantorId,
          payment_plan_id: PlanID
        }
      );
      setOpenDialogBox(false);
      if (cancelPaymentPlanResponse) {
        enqueueSnackbar(
          cancelPaymentPlanResponse.message ??
            t('PAYMENT_PLANS.PAYMENT_PLAN_CANCELLED'),
          SnackBarConfig('s')
        );
        forceUpdateHandler();
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      // throw error;
    } finally {
      setLoader(false);
      setOpenDialogBox(false);
    }
  };
  const renderCancelConfirmationDialog = () => {
    return (
      <ConfirmationDialog
        open={openDialogBox}
        onClose={response => {
          if (response === 'confirm') {
            handleCancelPaymentPlan(
              paymentPlanDetails?.inProgressPlans?.[0].id
            );
          } else {
            setOpenDialogBox(false);
          }
        }}
        title={t('PAYMENT_PLANS.CANCEL_PAYMENT_PLAN')}
        description={t('PAYMENT_PLANS.CANCEL_PLAN_DESCRIPTION')}
        loader={loader}
      />
    );
  };

  return (
    <Card
      className={[classes.root, 'payment-history-card-wrapper'].join(' ')}
      variant="outlined">
      <CardContent className={classes.content}>
        {/** First Row Container */}
        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            {
              <FieldValue
                label={t('PAYMENT_PLANS.FIRST_PAYMENT_DATE')}
                value={HelperUtils.formatDisplayDate(
                  activePlandetails?.instalment_start_date
                )}
              />
            }
          </Grid>
          <Grid item className={'flexing-items'}>
            <Grid item className={'flexing-items'}>
              {
                <FieldValue
                  label={t('PAYMENT_PLANS.LAST_PAYMENT_DATE')}
                  value={
                    previousPaymentPlanDate &&
                    previousPaymentPlanDate?.instalment_date
                      ? HelperUtils.formatDisplayDate(
                          previousPaymentPlanDate?.instalment_date
                        )
                      : '-'
                  }
                />
              }
            </Grid>
          </Grid>

          <Grid item className={'flexing-items'}>
            {
              <FieldValue
                label={t('PAYMENT_PLANS.NEXT_INSTALMENT_DATE')}
                value={
                  upcomingPaymentPlanDate &&
                  upcomingPaymentPlanDate?.instalment_date
                    ? HelperUtils.formatDisplayDate(
                        upcomingPaymentPlanDate?.instalment_date
                      )
                    : '-'
                }
              />
            }
          </Grid>
        </Grid>

        {/** Second Row Container (conditional) **/}
        {
          <Grid container className={classes.containerWrap} spacing={1}>
            <Grid item className={'flexing-items'}>
              <FieldValue
                label={t('PAYMENT_PLANS.TOTAL_INSTALMENTS')}
                value={activePlandetails?.tenure}
              />
            </Grid>

            <Grid item className="flexing-items">
              <FieldValue
                label={t('PAYMENT_PLANS.AMOUNT_PER_INSTALMENT')}
                value={
                  <CurrencyFormatter
                    value={activePlandetails?.instalment_amount}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                    ignoreRound={true}
                    useSign={true}
                  />
                }
              />
            </Grid>

            <Grid item className={'flexing-items'}>
              <FieldValue
                label={t('PAYMENT_PLANS.TOTAL_PLAN_AMOUNT')}
                value={
                  <CurrencyFormatter
                    value={totalPlanAmount}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                    ignoreRound={true}
                    useSign={true}
                  />
                }
              />
            </Grid>
          </Grid>
        }
        {/* Third row Container */}
        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.INSTALMENTS_PAID')}
              value={activePlandetails?.tenure_paid}
            />
          </Grid>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.TOTAL_PAID_AMOUNT')}
              value={
                <CurrencyFormatter
                  value={totalPaidAmount}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  ignoreRound={true}
                  useSign={true}
                />
              }
            />
          </Grid>
          <Grid
            item
            className={[classes.flexingItems, classes.statusStyle].join(' ')}>
            <FieldValue
              label={t('PAYMENT_PLANS.STATUS')}
              value={
                PaymentPlanStatus.IN_PROGRESS === activePlandetails?.status &&
                'Active'
              }
            />
            {!cancelPaymentPlan && (
              <Link
                variant="body2"
                className={classes.cancelPlanLinkStyle}
                type={'button'}
                component="button"
                disabled={loader}
                onClick={() => setOpenDialogBox(true)}>
                {t('PAYMENT_PLANS.CANCEL_PLAN_LINK')}
              </Link>
            )}
          </Grid>
        </Grid>

        {/*  FOURTH ROW..... */}
        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.FREQUENCY')}
              value={t(
                getPaymentPlanFrequencyLabel(activePlandetails?.frequency_id)
              )}
            />
          </Grid>

          {activePlandetails?.charge_day ? (
            <Grid item className={'flexing-items'}>
              <FieldValue
                label={t('PAYMENT_PLANS.INSTALMENT_CHARGE_DAY')}
                value={HelperUtils.captilizeFirstLetter(
                  activePlandetails.charge_day
                )}
              />
            </Grid>
          ) : (
            <Grid item className={'flexing-items'} />
          )}
          <Grid item className={'flexing-items'} />
        </Grid>
      </CardContent>
      {openDialogBox && renderCancelConfirmationDialog()}
    </Card>
  );
};

export default PaymentPlanCardDetails;
